"use client";

import React from "react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Info } from "lucide-react";
import { useTranslation } from "react-i18next";

interface CoPayCoverageSelectionProps {
  coPayCoveragePercentage: number;
  setCoPayCoveragePercentage: (percentage: number) => void;
  coPayCoverageOptions: number[];
  isLoadingPrice: boolean;
}

const CoPayCoverageSelection: React.FC<CoPayCoverageSelectionProps> = ({
  coPayCoveragePercentage,
  setCoPayCoveragePercentage,
  coPayCoverageOptions,
  isLoadingPrice,
}) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1">
        <Label htmlFor="co-pay-coverage" className="text-lg font-medium">
          {t("priceCustomization.coPayCoverage")}
        </Label>
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="ghost" size="icon" className="h-6 w-6">
              <Info className="h-4 w-4 text-muted-foreground" />
              <span className="sr-only">{t("common.info")}</span>
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("priceCustomization.coPayCoverageInfoTitle")}</DialogTitle>
              <DialogDescription
                dangerouslySetInnerHTML={{
                  __html: t("priceCustomization.coPayCoverageInfo")
                }}
              />                
            </DialogHeader>
          </DialogContent>
        </Dialog>
      </div>
      <Select
        value={String(coPayCoveragePercentage)}
        onValueChange={(value) => setCoPayCoveragePercentage(Number(value))}
        disabled={isLoadingPrice}
      >
        <SelectTrigger id="co-pay-coverage" className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {coPayCoverageOptions.map((coverage) => (
            <SelectItem key={coverage} value={String(coverage)}>
              {coverage}% {coverage === 100 && `(${t("priceCustomization.fullCoverage")})`}
              {coverage === 80 && `(${t("priceCustomization.standardCoverage")})`}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default CoPayCoverageSelection;
